import { LitElement, html, svg, nothing, type PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { StateTree, TreeChart } from '../components/model/state-tree';
import { layoutTree, lineY, type Box, type Drop, type PlacedLeaf, type PlacedMachine, type TreeLayout } from './statechart-tree-layout';

/*
 * <vitesse-statechart> — an SVG statechart renderer for SCXML.
 *
 * A from-scratch, lean replacement for the agent's StatechartLayout/Renderer.
 *   - parses SCXML once (DOMParser) into a state tree,
 *   - lays it out ONCE (memoized until the SCXML changes) with a recursive
 *     layered layout: DFS longest-path ranking per compound region (back-edges
 *     ignored so cyclic charts flow downward) + ROW WRAPPING — a rank whose
 *     siblings exceed the max row width wraps onto extra rows, so wide/deeply
 *     nested machines stay near the column width instead of growing
 *     unboundedly sideways. Compounds size to their content recursively.
 *   - renders pure SVG with the agent's class names (.state-container/
 *     .state-rect/.state-label/.transition-path/.arrow-path/…), so the global
 *     `_statechart-renderer.scss` styles it identically.
 *
 * NO pan/zoom: the SVG renders at the column's width (intrinsic aspect ratio)
 * and overflows vertically into the column's native scrollbar — scroll up/down
 * only, like the rest of the page. The initial state of a region is implicit
 * (its first/`initial` child ranks first); no initial-dot pseudo-state is drawn.
 * Active states (the model's live currentState) highlight via `.active`.
 *
 * Light DOM so the global SCSS applies. Visual-only; not a full SCXML
 * semantics engine.
 *
 * THE GHOST TREE (docs/state-subgraph-plan.md). With `selectable`, a click or
 * Enter on a state sends `state-select` (detail: the state id). The `selected`
 * state has an outline, and `tree` (state-tree.ts) hangs under it: level 1, the
 * machines that the state uses, and level 2, the statechart of each, laid out
 * with the same layout as the root. statechart-tree-layout.ts places them.
 */
type SType = 'scxml' | 'state' | 'parallel' | 'final' | 'history' | 'initial';
interface SNode {
  id: string;
  type: SType;
  states: SNode[];
  transitions: { event?: string; cond?: string; target?: string }[];
  initial?: string;
  // layout (filled in place)
  x: number;
  y: number;
  width: number;
  height: number;
}
interface LEdge {
  source: string;
  target: string;
  /** Full event descriptor (may be a space-separated list) — for the hover card. */
  event?: string;
  cond?: string;
  /** Display label: first event token, ellipsized — full text on hover. */
  label?: string;
  path: string;
  midX: number;
  midY: number;
}

const TITLE = 22; // compound title strip
const PADX = 14;
const PADY = 12;
const GAPX = 26;
const GAPY = 36;
const STATE_H = 34;
// Max content width of one region row before wrapping onto the next row. Kept
// near a half-width detail column so the chart reads WITHOUT shrinking the
// other column — width-constrained, height goes to the scrollbar.
const MAX_ROW_W = 600;

@customElement('vitesse-statechart')
export class VitesseStatechart extends LitElement {
  @property() scxml = '';
  /** Live active state id(s) — comma/space separated (model.currentState). */
  @property() active = '';
  /** Edges to light as "just fired": `edgeKey(source, target, event)` strings.
   *  The replay viewer sets it from the most recent transition at its cursor. */
  @property({ attribute: false }) firing: Iterable<string> = [];
  /** States to tag as never reached — the replay viewer's "never entered" list. */
  @property({ attribute: false }) neverEntered: readonly string[] = [];
  /** The tag's text; the viewer passes the translated string. */
  @property() neverEnteredLabel = 'never entered';
  /** A click or Enter on a state sends `state-select`: the ghost tree is on. */
  @property({ type: Boolean }) selectable = false;
  /** The selected state's id: it has an outline, and the ghost tree hangs under it. */
  @property() selected = '';
  /** The ghost tree under the selected state, or null for none. Drawn only when `tree.state` is `selected`. */
  @property({ attribute: false }) tree: StateTree | null = null;
  /** Where the ghost tree goes; null when there is none. */
  @state() private treeLayout: TreeLayout | null = null;
  @state() private root: SNode | null = null;
  @state() private edges: LEdge[] = [];
  // Hover details: which state/edge is under the pointer (+ where, viewport
  // coords) — drives the .hover-card and the blue highlight on the full edge.
  @state() private hoverNode: SNode | null = null;
  @state() private hoverEdge: LEdge | null = null;
  @state() private hoverXY = { x: 0, y: 0 };
  private nodeIndex = new Map<string, SNode>();

  protected createRenderRoot(): HTMLElement {
    return this; // light DOM → global _statechart-renderer.scss applies
  }

  protected willUpdate(changed: PropertyValues): void {
    if (changed.has('scxml')) this.build();
    if (changed.has('scxml') || changed.has('tree') || changed.has('selected')) this.layoutGhostTree();
  }

  private get activeSet(): Set<string> {
    return new Set(
      this.active
        .split(/[,\s]+/)
        .map((s) => s.trim())
        .filter(Boolean),
    );
  }
  private get firingSet(): ReadonlySet<string> {
    return this.firing instanceof Set ? this.firing : new Set(this.firing);
  }
  private get neverSet(): ReadonlySet<string> {
    return new Set(this.neverEntered);
  }

  // ── parse + layout (memoized on scxml change) ──────────────────────────────
  @state() private bounds = { x: 0, y: 0, w: 100, h: 100 };
  /** The root chart's own bounds. `bounds` adds the ghost tree's to them. */
  private rootBounds = { x: 0, y: 0, w: 100, h: 100 };
  /** Counts the layouts of the root's text: a ghost tree laid out over an older one is laid out again. */
  private rootVersion = 0;

  private build(): void {
    this.rootVersion++;
    // A new text is another model or a save: the charts of the templates are laid out again when used.
    this.charts.clear();
    const chart = buildChart(this.scxml);
    this.root = chart?.root ?? null;
    this.edges = chart?.edges ?? [];
    this.nodeIndex = chart?.index ?? new Map();
    if (chart) this.rootBounds = chart.bounds;
    this.bounds = this.rootBounds;
  }

  // ── the ghost tree (Step 6 of docs/state-subgraph-plan.md) ─────────────────
  /** The level-2 charts, laid out 1 time for each template text. */
  private charts = new Map<string, Chart | null>();
  /** What `treeLayout` is for. The live states are not in it: a live change moves only the `active` class. */
  private treeKey = '';

  private chartFor(scxml: string): Chart | null {
    if (!this.charts.has(scxml)) this.charts.set(scxml, buildChart(scxml));
    return this.charts.get(scxml) ?? null;
  }

  private layoutGhostTree(): void {
    const tree = this.tree;
    const state = this.selected ? this.nodeIndex.get(this.selected) : undefined;
    if (!tree || !state || tree.state !== this.selected) {
      this.treeKey = '';
      this.treeLayout = null;
      this.bounds = this.rootBounds;
      return;
    }
    const key = `${this.rootVersion}\n${this.selected}\n${JSON.stringify(tree, (k: string, v: unknown) => (k === 'active' || k === 'counts' ? undefined : v))}`;
    if (key === this.treeKey) return;
    this.treeKey = key;
    const others: Box[] = [];
    let right = 0;
    for (const n of this.nodeIndex.values()) {
      if (n.type === 'scxml') continue;
      right = Math.max(right, n.x + n.width);
      if (n !== state) others.push(boxOf(n));
    }
    const anchor = { state: boxOf(state), bottom: this.rootBounds.y + this.rootBounds.h, right, others };
    this.treeLayout = layoutTree(tree, anchor, (scxml) => this.chartFor(scxml)?.bounds ?? null);
    this.bounds = centredOn(this.rootBounds, union(this.rootBounds, this.treeLayout.bounds));
  }

  // ── selection (Step 5 of docs/state-subgraph-plan.md) ──────────────────────
  private select(id: string): void {
    if (!this.selectable) return;
    this.dispatchEvent(new CustomEvent('state-select', { detail: id, bubbles: true, composed: true }));
  }
  private readonly selectByKey = (e: KeyboardEvent, id: string): void => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    this.select(id);
  };

  // ── hover (state/transition details + full-path highlight) ─────────────────
  private readonly hoverState = (e: MouseEvent, node: SNode): void => {
    this.hoverNode = node;
    this.hoverEdge = null;
    this.hoverXY = { x: e.clientX, y: e.clientY };
  };
  private readonly hoverTransition = (e: MouseEvent, edge: LEdge): void => {
    this.hoverEdge = edge;
    this.hoverNode = null;
    this.hoverXY = { x: e.clientX, y: e.clientY };
  };
  private readonly hoverEnd = (): void => {
    this.hoverNode = null;
    this.hoverEdge = null;
  };

  private renderHoverCard(): unknown {
    const n = this.hoverNode;
    const ed = this.hoverEdge;
    if (!n && !ed) return nothing;
    // Keep the card on-screen near the pointer (it's position:fixed).
    const x = Math.min(this.hoverXY.x + 14, window.innerWidth - 330);
    const y = Math.min(this.hoverXY.y + 14, window.innerHeight - 200);
    return html`<div class="hover-card" style="left:${x}px;top:${y}px">
      ${n
        ? html`<div class="hover-title">${n.id}</div>
            <div class="hover-content">
              <div>${n.states.length ? `compound · ${n.states.length} substates` : n.type}${this.activeSet.has(n.id) ? ' · active' : ''}${this.neverSet.has(n.id) ? ` · ${this.neverEnteredLabel}` : ''}</div>
              ${n.transitions.length
                ? html`<div class="code">
                    ${n.transitions.map(
                      (t) =>
                        html`<div>${t.event ?? '(eventless)'}${t.cond ? ` [${t.cond}]` : ''} → ${t.target ?? '(internal)'}</div>`,
                    )}
                  </div>`
                : nothing}
            </div>`
        : nothing}
      ${ed
        ? html`<div class="hover-title">${ed.event ?? '(eventless)'}</div>
            <div class="hover-content">
              <div>${ed.source} → ${ed.target}</div>
              ${ed.cond ? html`<div class="code"><div>cond: ${ed.cond}</div></div>` : nothing}
            </div>`
        : nothing}
    </div>`;
  }

  // ── render ─────────────────────────────────────────────────────────────────
  private renderNode(node: SNode, level = 0): unknown {
    const kids = node.states.map((c) => this.renderNode(c, level + 1));
    const isNever = this.neverSet.has(node.id);
    if (node.type === 'final') {
      const cx = node.x + node.width / 2;
      const cy = node.y + node.height / 2;
      return svg`<g data-state-id=${node.id} class=${isNever ? 'never-entered' : ''}>
        <circle cx=${cx} cy=${cy} r="9" class="final-state-outer"></circle>
        <circle cx=${cx} cy=${cy} r="5" class="final-state-inner"></circle>
      </g>${kids}`;
    }
    const isActive = this.activeSet.has(node.id);
    const isHovered = this.hoverNode === node;
    const isSelected = this.selected === node.id;
    const pick = this.selectable;
    // The "never entered" tag sits ON the top edge, right-aligned, so it never
    // collides with the label (which is left-aligned and vertically centred)
    // and never widens the box. States are at least 72 wide; the tag is 62.
    const tagW = 62;
    const tagX = node.x + node.width - tagW - 4;
    return svg`<g data-state-id=${node.id} class=${`state-container${isActive ? ' active' : ''}${isNever ? ' never-entered' : ''}`}
        tabindex=${pick ? '0' : nothing} role=${pick ? 'button' : nothing} aria-label=${pick ? node.id : nothing}
        aria-pressed=${pick ? String(isSelected) : nothing}
        @click=${() => this.select(node.id)} @keydown=${(e: KeyboardEvent) => this.selectByKey(e, node.id)}
        @mouseenter=${(e: MouseEvent) => this.hoverState(e, node)} @mouseleave=${this.hoverEnd}>
        <rect x=${node.x} y=${node.y} width=${node.width} height=${node.height} rx="5"
          class=${`state-rect level-${level}${isActive ? ' active' : ''}${isHovered ? ' hovered' : ''}${isNever ? ' never-entered' : ''}`}></rect>
        ${
          isSelected
            ? svg`<rect class="state-selected-ring" x=${node.x - 3} y=${node.y - 3} width=${node.width + 6} height=${node.height + 6} rx="7"></rect>`
            : nothing
        }
        <text x=${node.x + 10} y=${node.y + (node.states.length ? 13 : node.height / 2)}
          class=${`state-label${isActive ? ' active' : ''}`} dominant-baseline="middle">${node.id}</text>
        ${
          isNever
            ? svg`<g class="never-badge-group">
                <rect class="never-badge-bg" rx="6" x=${tagX} y=${node.y - 7} width=${tagW} height="13"></rect>
                <text class="never-badge" x=${tagX + tagW / 2} y=${node.y - 0.5} text-anchor="middle" dominant-baseline="middle">${this.neverEnteredLabel}</text>
              </g>`
            : nothing
        }
      </g>${kids}`;
  }

  // Base edges are STABLE — hover never reorders or restyles them (reordering
  // the DOM under the cursor made hover fight/flicker between overlapping
  // edges). The blue highlight is a separate pointer-events:none OVERLAY
  // (renderHoverOverlay) drawn last, so it reads in the foreground without
  // touching hit-testing.
  private renderEdge(e: LEdge): unknown {
    // An edge in the `firing` set is the one that just fired at the replay's
    // cursor: green path, green arrowhead, green label. The stylesheet had
    // these rules before anything emitted them.
    const fires = edgeFires(this.firingSet, e.source, e.target, e.event);
    return svg`<g class="transition-group"
      @mouseenter=${(ev: MouseEvent) => this.hoverTransition(ev, e)} @mouseleave=${this.hoverEnd}>
      <path d=${e.path} class="transition-hover-area"></path>
      <path d=${e.path} class=${`transition-path default${fires ? ' active' : ''}`} marker-end=${fires ? 'url(#sc-arrow-active)' : 'url(#sc-arrow)'} pathLength="1"></path>
      ${
        e.label
          ? svg`<g transform=${`translate(${e.midX},${e.midY})`}>
              <rect class="transition-label-bg" rx="4" x=${-(e.label.length * 3.4 + 4)} y="-8"
                width=${e.label.length * 6.8 + 8} height="16"></rect>
              <text class=${`transition-label default${fires ? ' active' : ''}`} text-anchor="middle" dominant-baseline="middle">${e.label}</text>
            </g>`
          : nothing
      }
    </g>`;
  }

  private renderHoverOverlay(): unknown {
    const e = this.hoverEdge;
    if (!e) return nothing;
    return svg`<g style="pointer-events:none">
      <path d=${e.path} class="transition-path hovered" marker-end="url(#sc-arrow-hover)"></path>
      ${
        e.label
          ? svg`<g transform=${`translate(${e.midX},${e.midY})`}>
              <rect class="transition-label-bg hovered" rx="4" x=${-(e.label.length * 3.4 + 4)} y="-8"
                width=${e.label.length * 6.8 + 8} height="16"></rect>
              <text class="transition-label hovered" text-anchor="middle" dominant-baseline="middle">${e.label}</text>
            </g>`
          : nothing
      }
    </g>`;
  }

  /*
   * The ghost tree: level 1 (the trunk, the buses, the drops and the machine boxes) and level 2 (the
   * statechart of each machine), each in a group of its own opacity. The layout keeps the tree it was
   * made from, so the live states come from the current `tree`, by the machine's name.
   */
  private renderGhostTree(): unknown {
    const layout = this.treeLayout;
    if (!layout || !this.tree) return nothing;
    const live = new Map(this.tree.machines.map((m) => [m.name, m.chart]));
    return svg`<g class="ghost-tree">
      <g class="ghost-l1">
        <path class="ghost-line" d=${layout.trunk}></path>
        ${layout.buses.map((b) => svg`<path class="ghost-line" d=${`M ${b.x1} ${b.y} L ${b.x2} ${b.y}`}></path>`)}
        ${layout.spine ? svg`<path class="ghost-line" d=${`M ${layout.spine.x} ${layout.spine.y1} L ${layout.spine.x} ${layout.spine.y2}`}></path>` : nothing}
        ${layout.machines.map((p) => renderGhostMachine(p))}
        ${layout.leaves.map((l) => renderGhostLeaf(l))}
      </g>
      <g class="ghost-l2">${layout.machines.map((p) => this.renderGhostChart(p, live.get(p.machine.name)))}</g>
    </g>`;
  }

  private renderGhostChart(p: PlacedMachine, live: TreeChart | undefined): unknown {
    const chart = p.chart && p.machine.chart.scxml ? this.chartFor(p.machine.chart.scxml) : null;
    if (!p.chart || !chart) return nothing;
    const { dx, dy, link } = p.chart;
    const active = new Set(live?.active ?? []);
    // The link ends on the chart's initial state: the chart's bounds hold its edges' rails, so its
    // middle is not always over a state.
    const first = chart.root.states.find((s) => s.id === chart.root.initial) ?? chart.root.states[0];
    const tx = first ? dx + first.x + first.width / 2 : link.x;
    const ty = first ? dy + first.y : link.y2;
    const my = (link.y1 + ty) / 2;
    return svg`<path class="ghost-line" d=${`M ${link.x} ${link.y1} C ${link.x} ${my}, ${tx} ${my}, ${tx} ${ty}`}></path>
      <g transform=${`translate(${dx},${dy})`}>
        ${chart.root.states.map((s) => renderGhostState(s, 0, active, live?.counts))}
        ${chart.edges.map((e) => renderGhostEdge(e))}
      </g>`;
  }

  render() {
    if (!this.root) {
      return html`<div class="renderer-container">
        <div class="statechart-loading">${this.scxml ? 'Could not parse SCXML' : ''}</div>
      </div>`;
    }
    const { x, y, w, h } = this.bounds;
    // Natural-size SVG: width fills the column, height follows the intrinsic
    // aspect ratio, and the COLUMN's native scrollbar handles vertical overflow
    // (no pan/zoom — see _statechart-renderer.scss .statechart-scroll). The
    // max-width is not rounded: a rounded one gives the chart another scale when
    // the ghost tree changes the bounds, and the chart moves.
    return html`<div class="statechart-scroll">
      <svg
        class="statechart-svg"
        viewBox=${`${x} ${y} ${w} ${h}`}
        style=${`aspect-ratio:${(w / h).toFixed(4)};max-width:${(w * 1.15).toFixed(2)}px`}
        preserveAspectRatio="xMidYMin meet"
      >
        <defs>
          <marker id="sc-arrow" viewBox="0 0 10 10" markerWidth="8" markerHeight="8" refX="8" refY="5" orient="auto" markerUnits="strokeWidth">
            <path d="M0,0 L10,5 L0,10 L2,5 Z" class="arrow-path"></path>
          </marker>
          <marker id="sc-arrow-hover" viewBox="0 0 10 10" markerWidth="8" markerHeight="8" refX="8" refY="5" orient="auto" markerUnits="strokeWidth">
            <path d="M0,0 L10,5 L0,10 L2,5 Z" class="arrow-path-hover"></path>
          </marker>
          <marker id="sc-arrow-active" viewBox="0 0 10 10" markerWidth="8" markerHeight="8" refX="8" refY="5" orient="auto" markerUnits="strokeWidth">
            <path d="M0,0 L10,5 L0,10 L2,5 Z" class="arrow-path-active"></path>
          </marker>
          <marker id="sc-ghost-arrow" viewBox="0 0 10 10" markerWidth="8" markerHeight="8" refX="8" refY="5" orient="auto-start-reverse" markerUnits="strokeWidth">
            <path d="M0,0 L10,5 L0,10 L2,5 Z" class="arrow-path"></path>
          </marker>
        </defs>
        ${this.root.states.map((s) => this.renderNode(s))}
        ${this.edges.map((e) => this.renderEdge(e))}
        ${this.renderGhostTree()}
        ${this.renderHoverOverlay()}
      </svg>
      ${this.renderHoverCard()}
    </div>`;
  }
}

// ── layout of 1 chart: the root's, and each level-2 chart of the ghost tree ──
/** A chart laid out: its states at their absolute places, its edges, and its bounds with every label. */
interface Chart {
  root: SNode;
  edges: LEdge[];
  index: Map<string, SNode>;
  bounds: Box;
}

/** Parse and lay out a text; null when it does not parse, or is too large to draw. */
function buildChart(scxml: string): Chart | null {
  const tree = parseScxml(scxml);
  if (!tree) return null;
  // Safety cap: a pathological document (thousands of states) must degrade
  // to the parse-fail message, never hang layout or balloon the DOM.
  if (countNodes(tree) > 2000) return null;
  layoutNode(tree);
  const index = new Map<string, SNode>();
  absolutizeNode(tree, 0, 0, index);
  const edges = collectChartEdges(tree, index);
  // The bounds cover nodes AND every edge's label box / lane swing — labels
  // must never be cut off (computed, not a guessed pad).
  let minX = 0;
  let minY = 0;
  let maxX = tree.width;
  let maxY = tree.height;
  for (const e of edges) {
    const halfW = e.label ? (e.label.length * 6.8 + 8) / 2 : 6;
    minX = Math.min(minX, e.midX - halfW);
    maxX = Math.max(maxX, e.midX + halfW);
    minY = Math.min(minY, e.midY - 10);
    maxY = Math.max(maxY, e.midY + 10);
  }
  return { root: tree, edges, index, bounds: { x: minX - 8, y: minY - 8, w: maxX - minX + 16, h: maxY - minY + 16 } };
}

function layoutNode(node: SNode, depth = 0): void {
  if (node.states.length === 0) {
    node.width = node.type === 'final' ? 22 : textW(node.id);
    node.height = node.type === 'final' ? 22 : STATE_H;
    return;
  }
  const children = node.states;
  for (const c of children) layoutNode(c, depth + 1);

  // Ranking edges, PROJECTED to the sibling level: a transition from any
  // DESCENDANT of child A to any descendant of child B counts as A→B. Without
  // this, a compound whose only inbound edges come from leaves inside other
  // compounds has indegree 0 and floats to the top row — projection keeps the
  // visual flow (collecting → reporting → area1 → area2 → …) correct.
  const owner = new Map<string, string>();
  const claim = (n: SNode, top: string): void => {
    owner.set(n.id, top);
    for (const k of n.states) claim(k, top);
  };
  for (const c of children) claim(c, c.id);
  const edges: [string, string][] = [];
  const project = (n: SNode, top: string): void => {
    for (const t of n.transitions) {
      const o = t.target ? owner.get(t.target) : undefined;
      if (o && o !== top) edges.push([top, o]);
    }
    for (const k of n.states) project(k, top);
  };
  for (const c of children) project(c, c.id);
  // the region's initial child (explicit `initial` attr or first child) is the
  // implicit entry — it ranks first; no pseudo-state is drawn.
  const ids = new Set(children.map((c) => c.id));
  const initialId = node.initial && ids.has(node.initial) ? node.initial : children[0]?.id;
  const ranks = rankNodes(children, edges, initialId);

  const byRank = new Map<number, SNode[]>();
  for (const c of children) {
    const r = ranks.get(c.id) ?? 0;
    (byRank.get(r) ?? byRank.set(r, []).get(r)!).push(c);
  }
  const ranked = [...byRank.keys()].sort((a, b) => a - b).map((r) => byRank.get(r)!);
  const topPad = node.type === 'scxml' ? PADY : TITLE;

  // FLOW DIRECTION ALTERNATES BY DEPTH — the compactness move for deep
  // nesting: top-level regions stack vertically, their children flow
  // horizontally, THEIR children vertically again. A 3-deep plant (areas →
  // cells → steps) lays out as stacked areas of side-by-side cells of stacked
  // steps, instead of one enormous vertical chain — far more on screen.
  if (depth % 2 === 1) {
    // HORIZONTAL: each rank is a column (its nodes stacked); columns flow
    // left→right and band-wrap when wider than MAX_ROW_W.
    let x = PADX;
    let bandY = topPad;
    let bandH = 0;
    let maxX = 0;
    for (const ns of ranked) {
      const colW = Math.max(...ns.map((n) => n.width));
      const colH = ns.reduce((s, n) => s + n.height, 0) + (ns.length - 1) * GAPY;
      if (x > PADX && x + colW > PADX + MAX_ROW_W) {
        bandY += bandH + GAPY;
        x = PADX;
        bandH = 0;
      }
      let cy = bandY;
      for (const n of ns) {
        n.x = x + (colW - n.width) / 2;
        n.y = cy;
        cy += n.height + GAPY;
      }
      bandH = Math.max(bandH, colH);
      x += colW + GAPX;
      maxX = Math.max(maxX, x - GAPX);
    }
    node.width = maxX + PADX;
    node.height = bandY + bandH + PADY;
  } else {
    // VERTICAL: each rank is a row; an over-wide rank wraps onto extra rows.
    const rows: SNode[][] = [];
    for (const ns of ranked) {
      let cur: SNode[] = [];
      let w = 0;
      for (const n of ns) {
        const nw = n.width + (cur.length ? GAPX : 0);
        if (cur.length && w + nw > MAX_ROW_W) {
          rows.push(cur);
          cur = [n];
          w = n.width;
        } else {
          cur.push(n);
          w += nw;
        }
      }
      if (cur.length) rows.push(cur);
    }
    let y = topPad;
    let maxRowW = 0;
    const rowMeta: { nodes: SNode[]; w: number; h: number; y: number }[] = [];
    for (const ns of rows) {
      const w = ns.reduce((s, n) => s + n.width, 0) + (ns.length - 1) * GAPX;
      const h = Math.max(...ns.map((n) => n.height));
      rowMeta.push({ nodes: ns, w, h, y });
      maxRowW = Math.max(maxRowW, w);
      y += h + GAPY;
    }
    for (const row of rowMeta) {
      let x = PADX + (maxRowW - row.w) / 2;
      for (const n of row.nodes) {
        n.x = x;
        n.y = row.y + (row.h - n.height) / 2;
        x += n.width + GAPX;
      }
    }
    node.width = maxRowW + PADX * 2;
    node.height = y - GAPY + PADY;
  }
}

function absolutizeNode(node: SNode, ax: number, ay: number, index: Map<string, SNode>): void {
  node.x += ax;
  node.y += ay;
  index.set(node.id, node);
  for (const c of node.states) absolutizeNode(c, node.x, node.y, index);
}

function collectChartEdges(root: SNode, index: ReadonlyMap<string, SNode>): LEdge[] {
  interface Pending {
    s: SNode;
    t: SNode;
    event?: string;
    cond?: string;
    pairLane: number;
  }
  const pending: Pending[] = [];
  // Parallel edges between the same (source,target) pair get a LANE index so
  // their paths + labels fan out instead of stacking on the same midpoint.
  const pairLanes = new Map<string, number>();
  const walk = (n: SNode): void => {
    for (const t of n.transitions) {
      if (!t.target) continue;
      const tgt = index.get(t.target);
      if (!tgt) continue;
      const key = `${n.id}->${t.target}`;
      const lane = pairLanes.get(key) ?? 0;
      pairLanes.set(key, lane + 1);
      pending.push({ s: n, t: tgt, event: t.event, cond: t.cond, pairLane: lane });
    }
    for (const c of n.states) walk(c);
  };
  walk(root);

  // SIDE-ROUTED edges (back edges → left rail, row-skipping forward → right
  // rail) get GLOBAL rail numbers by y-span overlap, so every routed edge
  // runs on its own parallel rail with consistent curvature — no two side
  // edges cross/tangle and no two labels collide.
  const leftRails: { a: number; b: number; rail: number }[] = [];
  const rightRails: { a: number; b: number; rail: number }[] = [];
  const railFor = (placed: { a: number; b: number; rail: number }[], a: number, b: number): number => {
    let rail = 0;
    while (placed.some((p) => p.rail === rail && a < p.b && p.a < b)) rail++;
    placed.push({ a, b, rail });
    return rail;
  };
  return pending.map(({ s, t, event, cond, pairLane }) => {
    if (s !== t) {
      if (t.y + t.height <= s.y) {
        // back edge — left rail spanning [t.top, s.bottom]
        return edgePath(s, t, event, cond, railFor(leftRails, t.y, s.y + s.height));
      }
      if (t.y >= s.y + s.height && t.y - (s.y + s.height) > GAPY * 1.6) {
        // row-skipping forward — right rail spanning [s.top, t.bottom]
        return edgePath(s, t, event, cond, railFor(rightRails, s.y, t.y + t.height));
      }
    }
    return edgePath(s, t, event, cond, pairLane);
  });
}

const boxOf = (n: SNode): Box => ({ x: n.x, y: n.y, w: n.width, h: n.height });

function union(a: Box, b: Box): Box {
  const x = Math.min(a.x, b.x);
  const y = Math.min(a.y, b.y);
  return { x, y, w: Math.max(a.x + a.w, b.x + b.w) - x, h: Math.max(a.y + a.h, b.y + b.h) - y };
}

/**
 * `outer`, made wider on 1 side so that its middle is the middle of `inner`. The SVG is centred in
 * its column, so a ghost tree that reaches further to 1 side than the root chart would move the
 * root chart when the tree turns on. With the same middle, the root chart stays where it was.
 */
function centredOn(inner: Box, outer: Box): Box {
  const mid = inner.x + inner.w / 2;
  const half = Math.max(mid - outer.x, outer.x + outer.w - mid);
  return { x: mid - half, y: outer.y, w: 2 * half, h: outer.h };
}

// ── drawing of the ghost tree ────────────────────────────────────────────────
/** A drop from a bus to a column: the arrow into the box for a write, out of it for a read, and the ports. */
function renderDrop(d: Drop): unknown {
  const w = d.label.length * 6.8 + 8;
  const my = (d.y1 + d.y2) / 2;
  return svg`<path class="ghost-line" d=${`M ${d.x} ${d.y1} L ${d.x} ${d.y2}`}
      marker-start=${d.read ? 'url(#sc-ghost-arrow)' : nothing} marker-end=${d.write ? 'url(#sc-ghost-arrow)' : nothing}></path>
    ${
      d.label
        ? svg`<g transform=${`translate(${d.x},${my})`}>
            <rect class="transition-label-bg" rx="4" x=${-w / 2} y="-8" width=${w} height="16"></rect>
            <text class="ghost-port" text-anchor="middle" dominant-baseline="middle">${d.label}</text>
          </g>`
        : nothing
    }`;
}

function renderGhostMachine(p: PlacedMachine): unknown {
  const { box } = p;
  return svg`${renderDrop(p.drop)}
    <g class="ghost-node">
      <title>${p.tooltip}</title>
      <rect class="ghost-box" x=${box.x} y=${box.y} width=${box.w} height=${box.h} rx="6"></rect>
      ${p.lines.map(
        (l, i) => svg`<text class=${`ghost-${l.kind}`} x=${box.x + 10} y=${lineY(box, i)} dominant-baseline="middle">${l.text}</text>`,
      )}
    </g>`;
}

function renderGhostLeaf(l: PlacedLeaf): unknown {
  const { box } = l;
  return svg`${renderDrop(l.drop)}
    <g class="ghost-node">
      <title>${l.tooltip}</title>
      <rect class="ghost-box" x=${box.x} y=${box.y} width=${box.w} height=${box.h} rx=${box.h / 2}></rect>
      <text class="ghost-text" x=${box.x + box.w / 2} y=${box.y + box.h / 2} text-anchor="middle" dominant-baseline="middle">${l.text}</text>
    </g>`;
}

/** A state of a level-2 chart: lit when its machine is in it, with the count of an array's machines in it. */
function renderGhostState(node: SNode, level: number, active: ReadonlySet<string>, counts?: Readonly<Record<string, number>>): unknown {
  const kids = node.states.map((c) => renderGhostState(c, level + 1, active, counts));
  if (node.type === 'final') {
    const cx = node.x + node.width / 2;
    const cy = node.y + node.height / 2;
    return svg`<g>
      <circle cx=${cx} cy=${cy} r="9" class="final-state-outer"></circle>
      <circle cx=${cx} cy=${cy} r="5" class="final-state-inner"></circle>
    </g>${kids}`;
  }
  const n = counts?.[node.id] ?? 0;
  const on = active.has(node.id) || n > 0;
  const tag = String(n);
  const tagW = tag.length * 6 + 10;
  const tagX = node.x + node.width - tagW - 4;
  return svg`<g class=${`state-container${on ? ' active' : ''}`}>
      <rect x=${node.x} y=${node.y} width=${node.width} height=${node.height} rx="5"
        class=${`state-rect level-${level}${on ? ' active' : ''}`}></rect>
      <text x=${node.x + 10} y=${node.y + (node.states.length ? 13 : node.height / 2)}
        class=${`state-label${on ? ' active' : ''}`} dominant-baseline="middle">${node.id}</text>
      ${
        n > 0
          ? svg`<rect class="ghost-count-bg" rx="6" x=${tagX} y=${node.y - 7} width=${tagW} height="13"></rect>
              <text class="ghost-count" x=${tagX + tagW / 2} y=${node.y - 0.5} text-anchor="middle" dominant-baseline="middle">${tag}</text>`
          : nothing
      }
    </g>${kids}`;
}

function renderGhostEdge(e: LEdge): unknown {
  return svg`<path d=${e.path} class="transition-path default" marker-end="url(#sc-arrow)"></path>
    ${
      e.label
        ? svg`<g transform=${`translate(${e.midX},${e.midY})`}>
            <rect class="transition-label-bg" rx="4" x=${-(e.label.length * 3.4 + 4)} y="-8"
              width=${e.label.length * 6.8 + 8} height="16"></rect>
            <text class="transition-label default" text-anchor="middle" dominant-baseline="middle">${e.label}</text>
          </g>`
        : nothing
    }`;
}

// ── firing edges ──────────────────────────────────────────────────────────
/** The key the `firing` set is made of: `<source>→<target>@<event>`. `*` as
 *  the event matches an edge whatever its descriptor — a replay uses it for
 *  timer-fired transitions, whose delayed event name the trace does not carry. */
export function edgeKey(source: string, target: string, event: string): string {
  return `${source}→${target}@${event}`;
}

/** Does an edge fire for one of the keys? Descriptor matching follows SCXML:
 *  a descriptor token matches the event name exactly or as a dotted prefix,
 *  and `*` matches anything; an eventless edge answers to any event on its
 *  endpoints. */
export function edgeFires(firing: ReadonlySet<string>, source: string, target: string, descriptor?: string): boolean {
  if (firing.size === 0) return false;
  const prefix = `${source}→${target}@`;
  for (const key of firing) {
    if (!key.startsWith(prefix)) continue;
    const ev = key.slice(prefix.length);
    if (ev === '*' || !descriptor) return true;
    for (const tok of descriptor.split(/\s+/)) {
      if (tok === '*' || tok === ev || ev.startsWith(`${tok}.`)) return true;
    }
  }
  return false;
}

// ── helpers ───────────────────────────────────────────────────────────────
function textW(id: string): number {
  return Math.max(72, id.length * 7 + 20);
}

function countNodes(n: SNode): number {
  let total = 1;
  for (const c of n.states) total += countNodes(c);
  return total;
}

function parseScxml(xml: string): SNode | null {
  if (!xml || !xml.trim()) return null;
  let doc: Document;
  try {
    doc = new DOMParser().parseFromString(xml, 'application/xml');
  } catch {
    return null;
  }
  if (doc.querySelector('parsererror') || !doc.documentElement) return null;
  const STATE_TAGS = new Set(['state', 'parallel', 'final', 'history']);
  let counter = 0;
  const walk = (el: Element): SNode => {
    const tag = el.tagName.toLowerCase();
    const type = (tag === 'scxml' ? 'scxml' : tag) as SType;
    const states: SNode[] = [];
    const transitions: SNode['transitions'] = [];
    for (const child of Array.from(el.children)) {
      const ctag = child.tagName.toLowerCase();
      if (STATE_TAGS.has(ctag)) states.push(walk(child));
      else if (ctag === 'transition') {
        transitions.push({
          event: child.getAttribute('event') || undefined,
          cond: child.getAttribute('cond') || undefined,
          target: child.getAttribute('target') || undefined,
        });
      } else if (ctag === 'initial') {
        // an <initial> ELEMENT just names the entry child — fold its target
        // into the region's `initial` attr; nothing is drawn for it.
        const t = child.querySelector('transition');
        const target = t?.getAttribute('target');
        if (target && !el.getAttribute('initial')) el.setAttribute('initial', target);
      }
    }
    return {
      id: el.getAttribute('id') || (type === 'scxml' ? 'scxml' : `${type}_${counter++}`),
      type,
      states,
      transitions,
      initial: el.getAttribute('initial') || undefined,
      x: 0,
      y: 0,
      width: 0,
      height: 0,
    };
  };
  return walk(doc.documentElement);
}

// DFS longest-path layering. Statecharts are cyclic (back edges like
// running→idle), which breaks Kahn — so we DFS from the region's initial child
// (the implicit entry, rank 0) then any other roots, ignoring edges to a node
// already on the current stack (a back edge). Rank = longest forward depth.
function rankNodes(nodes: SNode[], edges: [string, string][], initialId?: string): Map<string, number> {
  const adj = new Map<string, string[]>();
  const indeg = new Map<string, number>();
  for (const n of nodes) {
    adj.set(n.id, []);
    indeg.set(n.id, 0);
  }
  for (const [u, v] of edges) {
    if (u === v) continue;
    adj.get(u)!.push(v);
    indeg.set(v, (indeg.get(v) ?? 0) + 1);
  }
  const rank = new Map<string, number>();
  const stack = new Set<string>();
  const dfs = (u: string, depth: number): void => {
    if ((rank.get(u) ?? -1) >= depth) return; // no improvement → prune
    rank.set(u, depth);
    stack.add(u);
    for (const v of adj.get(u) ?? []) if (!stack.has(v)) dfs(v, depth + 1);
    stack.delete(u);
  };
  if (initialId) dfs(initialId, 0); // the implicit entry ranks first
  for (const n of nodes) if ((indeg.get(n.id) ?? 0) === 0) dfs(n.id, 0);
  for (const n of nodes) if (!rank.has(n.id)) dfs(n.id, 0);
  return rank;
}

// Display label: the FIRST event token (an SCXML event attr may be a
// space-separated descriptor list — "recalibrate vitesse.recalibrate" — and the
// extra tokens are wiring detail, not display content), ellipsized. The full
// descriptor + cond stay on the hover card.
function labelFor(event?: string): string | undefined {
  if (!event) return undefined;
  const first = event.split(/\s+/)[0];
  return first.length > 22 ? `${first.slice(0, 21)}…` : first;
}

function edgePath(s: SNode, t: SNode, event?: string, cond?: string, lane = 0): LEdge {
  const label = labelFor(event);
  const base = { source: s.id, target: t.id, event, cond, label };
  if (s === t) {
    // self-loop — bows right; parallel self-loops bow farther out.
    const x = s.x + s.width;
    const y1 = s.y + s.height * 0.32;
    const y2 = s.y + s.height * 0.68;
    const bow = 36 + lane * 18;
    return {
      ...base,
      path: `M ${x} ${y1} C ${x + bow} ${y1 - 8}, ${x + bow} ${y2 + 8}, ${x} ${y2}`,
      midX: x + bow - 6,
      midY: (y1 + y2) / 2,
    };
  }
  if (t.y + t.height <= s.y) {
    // BACK edge (target above) — route around the LEFT side so it never
    // overlaps the forward column; parallel back edges take farther lanes.
    const xl = Math.min(s.x, t.x) - 26 - lane * 20;
    const sy = s.y + s.height / 2;
    const ty = t.y + t.height / 2;
    return {
      ...base,
      path: `M ${s.x} ${sy} C ${xl} ${sy}, ${xl} ${ty}, ${t.x} ${ty}`,
      midX: xl + 6,
      midY: (sy + ty) / 2,
    };
  }
  if (t.y >= s.y + s.height) {
    const sy = s.y + s.height;
    const ty = t.y;
    // ROW-SKIPPING forward edge (more than one row gap below) — a straight
    // vertical would pass THROUGH the intermediate row's boxes and its label
    // would collide with the short edges' — route around the RIGHT side
    // (back edges take the left).
    if (ty - sy > GAPY * 1.6) {
      const xr = Math.max(s.x + s.width, t.x + t.width) + 26 + lane * 20;
      const smy = s.y + s.height / 2;
      const tmy = t.y + t.height / 2;
      return {
        ...base,
        path: `M ${s.x + s.width} ${smy} C ${xr} ${smy}, ${xr} ${tmy}, ${t.x + t.width} ${tmy}`,
        midX: xr - 6,
        midY: (smy + tmy) / 2,
      };
    }
    // adjacent-row forward — vertical curve; parallel lanes fan out.
    const off = lane * 18;
    const sx = s.x + s.width / 2 + off;
    const tx = t.x + t.width / 2 + off;
    const dy = Math.max(28, (ty - sy) * 0.5);
    return {
      ...base,
      path: `M ${sx} ${sy} C ${sx} ${sy + dy}, ${tx} ${ty - dy}, ${tx} ${ty}`,
      midX: (sx + tx) / 2,
      midY: (sy + ty) / 2 + lane * 14,
    };
  }
  // same row — arc over the gap; parallel lanes arc higher.
  const sxr = t.x >= s.x ? s.x + s.width : s.x;
  const txl = t.x >= s.x ? t.x : t.x + t.width;
  const my = s.y + s.height / 2;
  const arc = 18 + lane * 16;
  return {
    ...base,
    path: `M ${sxr} ${my} C ${(sxr + txl) / 2} ${my - arc}, ${(sxr + txl) / 2} ${my - arc}, ${txl} ${t.y + t.height / 2}`,
    midX: (sxr + txl) / 2,
    midY: my - arc,
  };
}

declare global {
  interface HTMLElementTagNameMap {
    'vitesse-statechart': VitesseStatechart;
  }
}
